import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminMemberDetail, updateAdminMember, deleteAdminMember } from "@/api/adminMemberApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

export default function AdminMemberDetailPage() {
    const navigate = useNavigate()
    const { id } = useParams()

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)

    // 배송지 목록
    const [addresses, setAddresses] = useState<any[]>([])

    // 폼 상태 (수정 가능 필드)
    const [form, setForm] = useState({
        loginId: "",
        memberName: "",
        email: "",
        phone: "",
        role: "USER",
        status: "ACTIVE",
        createdAt: "",
        lastLoginAt: ""
    })

    useEffect(() => {
        const fetchDetail = async () => {
            if (!id) return;
            try {
                setLoading(true)
                const response = await getAdminMemberDetail(id)
                const data = response.data || response // ApiResponse 호환

                if (data.member) {
                    setForm({
                        loginId: data.member.loginId,
                        memberName: data.member.memberName,
                        email: data.member.email,
                        phone: data.member.phone || "",
                        role: data.member.role,
                        status: data.member.status,
                        createdAt: data.member.createdAt,
                        lastLoginAt: data.member.lastLoginAt || "로그인 이력 없음"
                    })
                }
                if (data.addresses) {
                    setAddresses(data.addresses)
                }
            } catch (error) {
                console.error(error)
                alert("회원 정보를 불러오는데 실패했습니다.")
                navigate("/admin/members")
            } finally {
                setLoading(false)
            }
        }
        fetchDetail()
    }, [id, navigate])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm(prev => ({ ...prev, [name]: value }))
    }

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!confirm("회원 정보를 수정하시겠습니까?")) return;

        try {
            setSaving(true)
            await updateAdminMember(id!, {
                memberName: form.memberName,
                email: form.email,
                phone: form.phone,
                role: form.role,
                status: form.status
            })
            alert("회원 정보가 성공적으로 수정되었습니다.")
        } catch (error) {
            console.error(error)
            alert("회원 정보 수정 중 오류가 발생했습니다.")
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!confirm("정말 이 회원을 삭제(탈퇴 처리)하시겠습니까?\n이 작업은 되돌릴 수 없으며, 관련 데이터 처리에 유의해야 합니다.")) return;

        try {
            setSaving(true)
            await deleteAdminMember(id!)
            alert("회원이 삭제되었습니다.")
            navigate("/admin/members")
        } catch (error) {
            console.error(error)
            alert("회원 삭제 중 오류가 발생했습니다. (연관된 데이터가 있을 수 있습니다)")
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex h-[50vh] items-center justify-center">
                    <p className="text-slate-500">회원 정보를 불러오는 중...</p>
                </div>
            </AdminLayout>
        )
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-3xl font-bold">회원 상세 정보</h2>
                        <p className="mt-2 text-sm text-slate-500">
                            회원 정보를 조회하고 수정할 수 있습니다.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" className="rounded-xl" onClick={() => navigate("/admin/members")}>
                            목록으로
                        </Button>
                        <Button variant="destructive" className="rounded-xl bg-red-500 hover:bg-red-600 text-white" onClick={handleDelete} disabled={saving}>
                            회원 삭제
                        </Button>
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
                    {/* 좌측: 회원 정보 폼 */}
                    <Card className="rounded-[28px] border border-slate-200 shadow-sm h-fit">
                        <CardHeader>
                            <CardTitle className="text-xl">기본 정보 수정</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleUpdate} className="grid gap-5 md:grid-cols-2">
                                <div className="space-y-2">
                                    <Label className="text-slate-500">아이디 (변경불가)</Label>
                                    <Input value={form.loginId} disabled className="h-12 rounded-xl bg-slate-50" />
                                </div>
                                <div className="space-y-2">
                                    <Label>이름</Label>
                                    <Input name="memberName" value={form.memberName} onChange={handleChange} className="h-12 rounded-xl" required />
                                </div>
                                <div className="space-y-2">
                                    <Label>이메일</Label>
                                    <Input name="email" type="email" value={form.email} onChange={handleChange} className="h-12 rounded-xl" required />
                                </div>
                                <div className="space-y-2">
                                    <Label>연락처</Label>
                                    <Input name="phone" value={form.phone} onChange={handleChange} className="h-12 rounded-xl" />
                                </div>
                                <div className="space-y-2">
                                    <Label>권한</Label>
                                    <select name="role" value={form.role} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                        <option value="USER">일반 사용자 (USER)</option>
                                        <option value="ADMIN">관리자 (ADMIN)</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label>계정 상태</Label>
                                    <select name="status" value={form.status} onChange={handleChange} className="h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none">
                                        <option value="ACTIVE">정상 (ACTIVE)</option>
                                        <option value="INACTIVE">정지 (INACTIVE)</option>
                                        <option value="SLEEP">휴면 (SLEEP)</option>
                                    </select>
                                </div>

                                <hr className="md:col-span-2 my-2 border-slate-100" />

                                <div className="space-y-2">
                                    <Label className="text-slate-500">가입일시</Label>
                                    <p className="text-sm font-medium text-slate-700 pt-2">{form.createdAt}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-slate-500">최근 로그인</Label>
                                    <p className="text-sm font-medium text-slate-700 pt-2">{form.lastLoginAt}</p>
                                </div>

                                <div className="md:col-span-2 flex justify-end mt-4">
                                    <Button type="submit" disabled={saving} className="rounded-xl h-12 px-8 bg-slate-900 hover:bg-slate-800 text-white">
                                        {saving ? "저장 중..." : "정보 수정하기"}
                                    </Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>

                    {/* 우측: 등록된 배송지 목록 */}
                    <Card className="rounded-[28px] border border-slate-200 shadow-sm h-fit">
                        <CardHeader>
                            <CardTitle className="text-xl">배송지 목록 ({addresses.length})</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {addresses.length === 0 ? (
                                <div className="flex h-32 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-500 border border-slate-200">
                                    등록된 배송지가 없습니다.
                                </div>
                            ) : (
                                addresses.map((addr) => (
                                    <div key={addr.addressId} className="relative rounded-2xl border border-slate-200 p-4 hover:bg-slate-50 transition-colors">
                                        {addr.defaultYn === 'Y' && (
                                            <span className="absolute top-4 right-4 bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded-md">
                                                기본배송지
                                            </span>
                                        )}
                                        <div className="space-y-1 pr-16">
                                            <p className="font-semibold text-slate-900">{addr.receiverName}</p>
                                            <p className="text-sm text-slate-600">{addr.receiverPhone}</p>
                                            <p className="text-sm text-slate-500 mt-2">
                                                [{addr.zipcode}] {addr.address1} {addr.address2}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </AdminLayout>
    )
}